import { useDispatch } from 'react-redux';
import React, { useCallback } from 'react';
import { Icon } from '@tcc/crate';

import { getMessage } from '@tcc/i18n';
import styles from './PanelCards.css';
import Header from '../Header';
import { PlantingCard } from '../../cards/PlantingCard';
import { HarvestCard } from '../../cards/HarvestCard/HarvestCard';
import ApplicationCardContainer from '../../cards/ApplicationCardContainer/ApplicationCardContainer';
import LinkCard from '../../../components/cards/LinkCard/LinkCard';
import FHACard from '../../cards/FHACard/FHACard';
import { FieldNotesCard } from '../../cards/FieldNotesCard/FieldNotesCard';
import {
  hasFha,
  hasWeather,
  hasScripting,
  onClickPrescriptions,
  hasFieldViewSpatialDisplay
} from '../../../utils/fieldFeatures';
import TrialRegionCard from '../../cards/TrialRegionCard/TrialRegionCard';
import { switchLayer } from '../../../utils/url';
import { WEATHER } from '../../../constants/mapLayerIds';
import { trackEvent } from '../../../actions/analyticsActions';
import { EnhancedScoutingCard } from '../../cards/EnhancedScoutingCard/EnhancedScoutingCard';
import RegionsCard from '../../cards/RegionsCard/RegionsCard';
import { getCurrentSeasonYear } from '../../../selectors/seasonSelectors';
import { useAppSelector } from '../../../utils';
import { getField, isFieldFeatureAvailable } from '../../../selectors';
import { FeatureFlagKey, Card, FieldPermission, FieldFeature } from '../../../types';
import { isSplitViewEnabledSelector } from '../../../selectors/splitViewSelectors';
import { SplitViewHeader } from '..';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';

export const SHOW_TRIAL_REGION_CARD_YEAR = 2021;

export type PanelCardsProps = {
  index: number;
  cards: Card[];
  testId: string;
  headerDetail?: React.ReactElement | string;
  isCollapsed?: boolean;
  firstCard?: React.ReactElement;
  lastCard?: React.ReactElement;
};

export const PanelCards = ({
  index,
  cards,
  headerDetail,
  isCollapsed,
  testId,
  firstCard,
  lastCard
}: PanelCardsProps) => {
  const dispatch = useDispatch();
  const state = useAppSelector(state => state);
  const seasonYear = parseInt(
    useAppSelector(state => getCurrentSeasonYear(state, { index })),
    10
  );
  const field = useAppSelector(getField);
  const isSplitViewEnabled = useAppSelector(isSplitViewEnabledSelector);
  const hideSpatialDisplayFeatureLDFlag = useFeatureFlag(FeatureFlagKey.DMB_HIDE_SPATIAL_DISPLAY_FEATURE);
  const fieldHasFieldRegionReportFeature = isFieldFeatureAvailable(state, FieldFeature.FIELD_REGION_REPORT);
  const showScoutingFeature = isFieldFeatureAvailable(state, FieldFeature.SCOUTING);

  const canSeeTrialRegionData = useFeatureFlag(FeatureFlagKey.DELARO_TRIAL_BLOCK);
  const isCommercialPolicyFieldRegionReportFFEnabled = useFeatureFlag(FeatureFlagKey.COMMERCIAL_POLICY_FIELD_REGION_REPORT);
  const shouldShowFRRPaywall = isCommercialPolicyFieldRegionReportFFEnabled && !fieldHasFieldRegionReportFeature;

  const handleClickPrescriptions = useCallback(() => {
    dispatch(trackEvent('prescriptions_planting_context_card', 'clicked'));
    onClickPrescriptions(field.id ?? '')();
  }, [field, dispatch]);

  const getCardComponents = () => {
    const prescriptionLinkCard = (
      <LinkCard
        icon={Icon.set.PRESCRIPTION}
        isVisible={hasScripting(field.features)}
        key="prescription-link-card"
        onClick={handleClickPrescriptions}
        text={getMessage('card.prescriptions')}
      />
    );

    return cards.map((card) => {
      switch (card) {
        case Card.APPLICATION: {
          return <ApplicationCardContainer index={index} key="applicationCard" />;
        }
        case Card.FHA: {
          const showFHA = hasFha(field.features);

          return showFHA ? (
            <FHACard index={index} key="fha-link-card" />
          ) : null;
        }
        case Card.HARVEST: {
          const hideHarvest = hideSpatialDisplayFeatureLDFlag && !hasFieldViewSpatialDisplay(field.features);

          if (hideHarvest) return null;
          if ([FieldPermission.OWNER, FieldPermission.EDIT].includes(field.permission)) {
            return <HarvestCard index={index} key="harvestCard" />;
          }
          return null;
        }
        case Card.PLANTING: {
          const hidePlanting = hideSpatialDisplayFeatureLDFlag && !hasFieldViewSpatialDisplay(field.features);

          if (hidePlanting) return null;
          return <PlantingCard index={index} />;
        }
        case Card.PRESCRIPTIONS: {
          return prescriptionLinkCard;
        }
        case Card.WEATHER: {
          const handleClick = () => {
            switchLayer(0, WEATHER);

            dispatch(trackEvent('weather_context_card', 'selected', {
              toggleResponseType: 'opened',
            }));
          };

          return !isSplitViewEnabled ? (
            <LinkCard
              icon={Icon.set.WEATHER}
              isVisible={hasWeather(field.features)}
              key="weather-link-card"
              onClick={handleClick}
              text={getMessage('card.weather')}
            />
          ) : null;
        }
        default: {
          return null;
        }
      }
    });
  };

  let header = <Header index={index} />;
  let cardsStyles = styles.cards;
  if (isSplitViewEnabled) {
    header = <SplitViewHeader headerDetail={headerDetail} index={index} />;
    cardsStyles = isCollapsed
      ? styles.cardsSplitCollapsed
      : styles.cardsSplitExpanded;
  }
  const shouldShowTrialRegionCard = seasonYear >= SHOW_TRIAL_REGION_CARD_YEAR && canSeeTrialRegionData;

  return (
    <div data-test-id={`${testId}-panel`}>
      {header}
      <div className={cardsStyles}>
        <div className={styles.container}>
          <> {/* eslint-disable-line */}
            {firstCard}
            {getCardComponents()}
            {showScoutingFeature && <>
              <EnhancedScoutingCard index={index} />
              <FieldNotesCard index={index} />
              <RegionsCard index={index} showPaywall={shouldShowFRRPaywall} />
            </>}
            {shouldShowTrialRegionCard && <TrialRegionCard />}
            {lastCard}
          </>
        </div>
      </div>
    </div>
  );
};
